import { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI, usersAPI, notificationsAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useSocket, useTypingIndicator } from '../hooks/useSocket';
import { format, isToday, isYesterday } from 'date-fns';

import { useToast, apiError } from '../components/Toast';
import { isAdminRole } from '../utils/rolesConfig';
import { AtSign, Check, CheckCheck, Circle, File, Hash, Menu, Paperclip, Plus, Send, Settings, Trash2, User, Users, X } from 'lucide-react';

// ── Icons ─────────────────────────────────────────────────────────────────
const ClipIc = () => <Paperclip size={16} />;
const TrashIc = () => <Trash2 size={12} />;
const TrashLg = () => <Trash2 size={15} />;
const EmojiIc = () => <Circle size={18} />;

// ── Single tick for DMs ───────────────────────────────────────────────────
// Read receipts. One tick sent, two ticks read — the same lucide glyph the
// rest of the app uses for a tick, at the size the bubble needs.
const SingleTick = ({ color = '#9ca3af' }) => <Check size={14} color={color} strokeWidth={2.5} />;
const DoubleTick = ({ color = '#9ca3af' }) => <CheckCheck size={16} color={color} strokeWidth={2.5} />;

// ── DM Ticks: single = sent, double grey = delivered, double blue = seen ──
function DMTicks({ msg }) {
  const readBy = Array.isArray(msg.read_by) ? msg.read_by : [];
  if (readBy.length > 0) return <DoubleTick color="#60a5fa" />;
  return <SingleTick />;
}

// ── Group read receipt: single tick OR stacked mini avatars on hover ──────
function GroupReadReceipt({ msg, groupMembers, currentUserId }) {
  const readBy = (Array.isArray(msg.read_by) ? msg.read_by : [])
    .filter(r => r.id !== msg.sender_id);

  if (readBy.length === 0) {
    return <SingleTick />;
  }

  const shown = readBy.slice(0, 3);
  const extra = readBy.length - shown.length;

  return (
    <span className="relative group/seen inline-flex items-center cursor-default">
      <span className="flex -space-x-1">
        {shown.map(r => (
          <span key={r.id}
            className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold border-2"
            style={{ background: r.avatar_color || '#2E539F', fontSize: 8, borderColor: 'var(--surface)' }}>
            {r.name?.[0]?.toUpperCase()}
          </span>
        ))}
        {extra > 0 && (
          <span className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold border-2 bg-gray-500"
            style={{ fontSize: 8, borderColor: 'var(--surface)' }}>
            +{extra}
          </span>
        )}
      </span>

      <span className="absolute bottom-6 right-0 z-50 hidden group-hover/seen:flex flex-col gap-1.5 rounded-xl px-3 py-2.5 shadow-2xl min-w-max pointer-events-none"
        style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <span className="text-xs font-semibold pb-1 border-b border-white/10" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Seen by {readBy.length} of {groupMembers.filter(m => m.id !== msg.sender_id).length}
        </span>
        {readBy.map(r => (
          <span key={r.id} className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-md flex items-center justify-center text-white font-bold flex-shrink-0"
              style={{ background: r.avatar_color || '#2E539F', fontSize: 10 }}>
              {r.name?.[0]?.toUpperCase()}
            </span>
            <span className="text-xs text-white">{r.name}</span>
            {r.read_at && <span className="text-xs ml-auto pl-4" style={{ color: 'rgba(255,255,255,0.35)' }}>{new Date(r.read_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>}
          </span>
        ))}
      </span>
    </span>
  );
}

// ── Emoji picker ──────────────────────────────────────────────────────────
const EMOJI_GROUPS = [
  { label: 'Smileys',  emojis: ['😀','😂','😍','🤔','😎','😭','🥰','😤','🤣','😊','😅','😉','😜','🤗','😏','😒','😔','😢','😡','🥳','🤩','😬','🙄','😴','🤯','🥺','😇','🫡'] },
  { label: 'Gestures', emojis: ['👍','👎','👌','✌️','🤞','👋','🤝','👏','🙌','🤜','🤛','💪','🫶','❤️','🔥','✅','❌','⭐','🎉','🙏','👀','💯','🚀','💡','⚡','🎯','💬','📌'] },
  { label: 'People',   emojis: ['🧑‍💻','👨‍💼','👩‍💼','🧑‍🏫','👨‍🔧','👩‍🔧','🧑‍💬','👨‍🎓','👩‍🎓','🏃','🤸','🧘','🎤','🎨','✍️','🤓','👑','🎭','🫂','🤦','🙆','💁','🤷','🧠'] },
  { label: 'Objects',  emojis: ['💻','📱','🖥️','⌨️','🖱️','📊','📈','📉','📋','📝','📁','🗂️','🔍','🔒','🔓','🛠️','⚙️','📧','📅','⏰','💰','🏆','🎖️','📢','🔔','🔕','🗑️','💾'] },
];

function EmojiPicker({ onSelect, onClose }) {
  const [tab, setTab] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  return (
    <div ref={ref}
      className="absolute bottom-14 left-0 z-50 rounded-2xl shadow-2xl overflow-hidden w-80"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        {EMOJI_GROUPS.map((g, i) => (
          <button key={i} onClick={() => setTab(i)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${tab === i ? 'border-b-2 border-brand-500 text-brand-500' : ''}`}
            style={tab !== i ? { color: 'var(--text-secondary)' } : {}}>
            {g.label}
          </button>
        ))}
      </div>
      <div className="p-3 grid grid-cols-7 gap-1 max-h-52 overflow-y-auto">
        {EMOJI_GROUPS[tab].emojis.map((e, i) => (
          <button key={i} onClick={() => onSelect(e)}
            className="w-9 h-9 flex items-center justify-center text-xl rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
            title={e}>
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── @mention autocomplete dropdown ─────────────────────────────────────────
function MentionDropdown({ candidates, activeIndex, onSelect }) {
  if (candidates.length === 0) return null;
  return (
    <div className="absolute bottom-14 left-0 z-50 rounded-xl shadow-2xl overflow-hidden w-64"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="px-3 py-2 border-b text-xs font-semibold flex items-center gap-1.5" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
        <AtSign size={14}/> Mention someone
      </div>
      <div className="max-h-52 overflow-y-auto py-1">
        {candidates.map((m, i) => (
          <button key={m.id} onClick={() => onSelect(m)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${i===activeIndex ? 'bg-brand-500/15' : 'hover:bg-[var(--surface-alt)]'}`}>
            <div className="w-6 h-6 rounded flex items-center justify-center font-bold text-white text-xs flex-shrink-0" style={{ background: m.avatar_color||'#2E539F' }}>
              {m.name?.[0]?.toUpperCase()}
            </div>
            <span className="text-sm" style={{ color:'var(--text)' }}>{m.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── File preview before sending ────────────────────────────────────────────
function FileBadge({ file, onRemove }) {
  const isImg   = file.type.startsWith('image/');
  const isVid   = file.type.startsWith('video/');
  const preview = isImg ? URL.createObjectURL(file) : null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border text-sm relative"
      style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }}>
      {isImg && preview && <img src={preview} alt="" className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />}
      {isVid && <span className="text-2xl">🎬</span>}
      {!isImg && !isVid && <File size={16}/>}
      <span className="truncate max-w-[120px] text-xs" style={{ color: 'var(--text)' }}>{file.name}</span>
      <button onClick={onRemove} className="ml-auto flex-shrink-0 text-red-400 hover:text-red-500"><X size={14}/></button>
    </div>
  );
}

// ── Misc helpers ──────────────────────────────────────────────────────────
const Av = ({ name, color, size = 'sm' }) => {
  const s = size === 'sm' ? 'w-7 h-7 text-xs' : size === 'md' ? 'w-9 h-9 text-sm' : 'w-10 h-10 text-sm';
  return (
    <div className={`${s} rounded-lg flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ background: color || '#2E539F' }}>
      {name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
    </div>
  );
};
const fmtTime = ts => { const d = new Date(ts); if (isToday(d)) return format(d,'HH:mm'); if (isYesterday(d)) return `Yesterday ${format(d,'HH:mm')}`; return format(d,'MMM d, HH:mm'); };
const fmtDiv  = ts => { const d = new Date(ts); if (isToday(d)) return 'Today'; if (isYesterday(d)) return 'Yesterday'; return format(d,'EEEE, MMMM d'); };
const showDiv = (msgs, i) => i === 0 || new Date(msgs[i].created_at).toDateString() !== new Date(msgs[i-1].created_at).toDateString();

// Detects "@partialname" ending exactly at the cursor position
const detectMentionTrigger = (value, cursorPos) => {
  const upToCursor = value.slice(0, cursorPos);
  const match = upToCursor.match(/@([a-zA-Z0-9_ ]{0,30})$/);
  if (!match) return null;
  return { query: match[1], start: cursorPos - match[0].length, end: cursorPos };
};

// ── Main component ────────────────────────────────────────────────────────
export default function ChatPage() {
  const toast = useToast();
  const { user } = useAuth();
  const socket = useSocket();
  const [groups,          setGroups]          = useState([]);
  const [active,          setActive]          = useState(null);
  const [messages,        setMessages]        = useState([]);
  const [text,            setText]            = useState('');
  const [sending,         setSending]         = useState(false);
  const [msgLoading,      setMsgLoading]      = useState(false);
  const [allUsers,        setAllUsers]        = useState([]);
  const [showNewGroup,    setShowNewGroup]    = useState(false);
  const [showNewDM,       setShowNewDM]       = useState(false);
  const [showManage,      setShowManage]      = useState(false);
  const [showEmoji,       setShowEmoji]       = useState(false);
  const [pendingFile,     setPendingFile]     = useState(null);
  const [newGroupName,    setNewGroupName]    = useState('');
  const [newGroupDesc,    setNewGroupDesc]    = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [creating,        setCreating]        = useState(false);
  const [deleteConfirm,   setDeleteConfirm]   = useState(false);
  const [replyTo,         setReplyTo]         = useState(null);
  const [sidebarOpen,     setSidebarOpen]     = useState(true);
  const [mention,         setMention]         = useState(null); // { query, start, end } or null
  const [mentionIndex,    setMentionIndex]    = useState(0);
  const endRef   = useRef(null);
  const fileRef  = useRef(null);
  const textRef  = useRef(null);
  const pollRef  = useRef(null);
  const typingTimeoutRef = useRef(null);

  // ── Real-time: who's typing in the active conversation ─────────────────
  const typingUsers = useTypingIndicator(socket, active?.id);

  // ── Data loading ──────────────────────────────────────────────────────
  const loadGroups = useCallback(async () => {
    try { const r = await chatAPI.getGroups(); setGroups(r.data||[]); return r.data||[]; }
    catch(e) { toast.error(apiError(e)); return []; }
  }, []);

  const lastMsgTs = useRef(null);

  const loadMsgs = useCallback(async (gid, opts = {}) => {
    if (!gid) return;
    if (!opts.incremental) setMsgLoading(true);
    try {
      const params = opts.incremental && lastMsgTs.current
        ? { after: lastMsgTs.current }
        : { limit: 100 };
      const r = await chatAPI.getMessages(gid, params);
      const newMsgs = r.data || [];
      if (opts.incremental) {
        if (newMsgs.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const toAdd = newMsgs.filter(m => !existingIds.has(m.id));
            if (toAdd.length === 0) return prev;
            setTimeout(() => endRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
            return [...prev, ...toAdd];
          });
          setMessages(prev => prev.map(m => {
            const updated = newMsgs.find(nm => nm.id === m.id);
            return updated ? { ...m, read_by: updated.read_by } : m;
          }));
        }
      } else {
        setMessages(newMsgs);
        if (newMsgs.length > 0) {
          lastMsgTs.current = newMsgs[newMsgs.length - 1].created_at;
        }
        setTimeout(() => endRef.current?.scrollIntoView({ behavior:'smooth' }), 80);
      }
      if (opts.incremental && newMsgs.length > 0) {
        lastMsgTs.current = newMsgs[newMsgs.length - 1].created_at;
      }
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); } finally { if (!opts.incremental) setMsgLoading(false); }
  }, []);

  useEffect(() => {
    loadGroups();
    usersAPI.getAll().then(r=>setAllUsers(r.data)).catch(e => toast.error(apiError(e, 'Could not load this page.')));
    // Mark only chat-category notifications read (not assignments/approvals) -
    // this moved to notifications.js when the Notifications page was built.
    notificationsAPI.markAllRead('chat').catch(() => {});
  }, [loadGroups]);

  // ── Real-time message push via Socket.io ───────────────────────────────
  // Joins/leaves the active conversation's room, and appends any message
  // pushed instantly by the server - no waiting for the next poll tick.
  useEffect(() => {
    if (!socket || !active) return;
    socket.emit('join_group', active.id);

    const onNewMessage = (msg) => {
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev; // already have it (e.g. it's our own send)
        lastMsgTs.current = msg.created_at;
        setTimeout(() => endRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
        return [...prev, msg];
      });
      loadGroups();
    };
    socket.on('new_message', onNewMessage);

    return () => {
      socket.emit('leave_group', active.id);
      socket.off('new_message', onNewMessage);
    };
  }, [socket, active?.id, loadGroups]);

  // Slower polling kept as a fallback safety net (e.g. if the socket drops) -
  // 3s incremental fetch is no longer the primary delivery path, Socket.io is.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (active) {
      let groupPollCounter = 0;
      pollRef.current = setInterval(() => {
        loadMsgs(active.id, { incremental: true });
        groupPollCounter++;
        if (groupPollCounter >= 5) {
          groupPollCounter = 0;
          loadGroups().then(upd => {
            setGroups(prev => {
              const changed = upd.some(g => {
                const old = prev.find(p => p.id === g.id);
                return !old || old.unread_count !== g.unread_count;
              });
              return changed ? upd : prev;
            });
          });
        }
      }, 8000); // slower now that Socket.io carries the real-time load
    }
    return () => clearInterval(pollRef.current);
  }, [active?.id, loadMsgs, loadGroups]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const getDMName  = g => g.type!=='direct' ? g.name : (g.members||[]).find(m=>m.id!==user?.id)?.name||g.name;
  const getDMColor = g => g.type!=='direct' ? null    : (g.members||[]).find(m=>m.id!==user?.id)?.avatar_color;
  const canDelete  = g => g && g.type==='group' && (g.created_by===user?.id || isAdminRole(user?.role));

  const selectGroup = useCallback((g) => {
    lastMsgTs.current = null;
    setMention(null);
    setActive(g); loadMsgs(g.id); setSidebarOpen(false); setShowManage(false); setDeleteConfirm(false);
  }, [loadMsgs]);

  // ── @mention candidates, filtered against the active conversation's members ──
  const mentionCandidates = mention
    ? (active?.members || [])
        .filter(m => m.id !== user?.id && m.name.toLowerCase().includes(mention.query.toLowerCase()))
        .slice(0, 6)
    : [];

  const selectMention = (member) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after  = text.slice(mention.end);
    const newText = `${before}@${member.name} ${after}`;
    setText(newText);
    setMention(null);
    const cursorPos = before.length + member.name.length + 2;
    setTimeout(() => { textRef.current?.focus(); textRef.current?.setSelectionRange(cursorPos, cursorPos); }, 0);
  };

  // ── Typing indicator emission (debounced stop) ──────────────────────────
  const emitTyping = () => {
    if (!socket || !active) return;
    socket.emit('typing_start', active.id);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => socket.emit('typing_stop', active.id), 2000);
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    emitTyping();
    const trigger = detectMentionTrigger(value, e.target.selectionStart);
    if (trigger) { setMention(trigger); setMentionIndex(0); }
    else setMention(null);
  };

  const handleKeyDown = (e) => {
    if (mention && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i+1) % mentionCandidates.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex(i => (i-1+mentionCandidates.length) % mentionCandidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionCandidates[mentionIndex]); return; }
      if (e.key === 'Escape') { setMention(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // ── Messaging ─────────────────────────────────────────────────────────
  const send = async () => {
    const hasText = text.trim();
    const hasFile = !!pendingFile;
    if ((!hasText && !hasFile) || !active) return;
    setSending(true);
    setMention(null);
    if (socket && active) socket.emit('typing_stop', active.id);
    const t = text; setText(''); const f = pendingFile; setPendingFile(null);
    try {
      let r;
      if (hasFile) {
        const fd = new FormData(); fd.append('file', f);
        if (replyTo) fd.append('reply_to', replyTo.id);
        if (hasText) fd.append('content', t);
        r = await chatAPI.sendFile(active.id, fd);
      } else {
        r = await chatAPI.sendText(active.id, t, replyTo?.id);
      }
      // Dedup guard: socket broadcast often arrives before this HTTP response
      // so check by id before appending to avoid the duplicate bubble.
      setMessages(p => p.some(m => m.id === r.data.id) ? p : [...p, { ...r.data, read_by: [] }]);
      lastMsgTs.current = r.data.created_at;
      setReplyTo(null); loadGroups();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
    } catch(e) { toast.error(apiError(e, 'Failed to send message.')); setText(t); setPendingFile(f); }
    finally { setSending(false); }
  };

  const delMsg = async id => {
    try { await chatAPI.deleteMessage(id); setMessages(p=>p.filter(m=>m.id!==id)); }
    catch(e) { toast.error(apiError(e, 'Failed to delete message.')); }
  };

  const deleteChannel = async () => {
    try {
      await chatAPI.deleteGroup(active.id);
      setActive(null); setMessages([]); setDeleteConfirm(false); setShowManage(false);
      await loadGroups();
    } catch(e) { toast.error(apiError(e, 'Failed to delete channel')); }
  };

  const insertEmoji = (emoji) => {
    setText(t => t + emoji);
    setShowEmoji(false);
    textRef.current?.focus();
  };

  // ── Create group / DM ─────────────────────────────────────────────────
  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreating(true);
    try {
      const r = await chatAPI.createGroup({ name:newGroupName, description:newGroupDesc, type:'group', member_ids:selectedMembers });
      setShowNewGroup(false); setNewGroupName(''); setNewGroupDesc(''); setSelectedMembers([]);
      const upd = await loadGroups();
      const ng = upd.find(g=>g.id===r.data.id); if (ng) selectGroup(ng);
    } catch(e) { toast.error(apiError(e, 'Failed. Please try again.')); } finally { setCreating(false); }
  };

  const createDM = async uid => {
    setCreating(true);
    try {
      const r = await chatAPI.createGroup({ name: allUsers.find(u=>u.id===uid)?.name||'DM', type:'direct', member_ids:[uid] });
      setShowNewDM(false);
      const upd = await loadGroups();
      const found = upd.find(g=>g.id===r.data.id); if (found) selectGroup(found);
    } catch(e) { toast.error(apiError(e, 'Failed. Please try again.')); } finally { setCreating(false); }
  };

  const channels = groups.filter(g=>g.type==='group');
  const dms      = groups.filter(g=>g.type==='direct');

  const GroupItem = ({ g }) => (
    <button onClick={()=>selectGroup(g)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active?.id===g.id?'bg-brand-500/15':'hover:bg-[var(--surface)]'}`}>
      {g.type==='group'
        ? <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:'rgba(99,102,241,.15)',color:'#2E539F' }}><Hash size={14}/></div>
        : <Av name={getDMName(g)} color={getDMColor(g)} />
      }
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color:'var(--text)' }}>{getDMName(g)}</p>
        {g.last_message && <p className="text-xs truncate" style={{ color:'var(--text-secondary)' }}>{g.last_message.message_type==='file'?`📎 ${g.last_message.file_name}`:g.last_message.message_type==='video'?'🎬 Video':g.last_message.message_type==='image'?'📷 Photo':g.last_message.content}</p>}
      </div>
      {parseInt(g.unread_count)>0 && <span className="text-xs bg-brand-500 text-white rounded-full px-1.5 py-0.5 font-bold flex-shrink-0 min-w-[20px] text-center">{g.unread_count}</span>}
    </button>
  );

  // Typing label, e.g. "Jane is typing..." or "Jane and Marc are typing..."
  const typingLabel = (() => {
    if (!typingUsers || typingUsers.length === 0) return null;
    const names = typingUsers.map(t => t.userName.split(' ')[0]);
    if (names.length === 1) return `${names[0]} is typing...`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
    return `${names.length} people are typing...`;
  })();

  return (
    <div className="flex rounded-xl overflow-hidden border" style={{ height:'calc(100vh - 3rem)', borderColor:'var(--border)', background:'var(--surface)' }}>

      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <div className={`flex flex-col border-r flex-shrink-0 transition-all ${sidebarOpen?'w-72':'w-0 overflow-hidden'} lg:w-72`}
        style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
        <div className="p-4 border-b" style={{ borderColor:'var(--border)' }}>
          <h2 className="font-bold mb-3" style={{ color:'var(--text)' }}>Messages</h2>
          <div className="flex gap-2">
            <button onClick={()=>setShowNewGroup(true)} className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-lg btn-primary"><Plus size={14}/> New Group</button>
            <button onClick={()=>setShowNewDM(true)} className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded-lg btn-secondary"><User size={14}/> Direct Message</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 pb-1 pt-2">
            <p className="text-xs font-bold uppercase tracking-wider mb-1.5 px-1" style={{ color:'var(--text-secondary)' }}>Channels ({channels.length})</p>
            {channels.length===0 ? <p className="text-xs px-2 py-2 italic" style={{ color:'var(--text-secondary)' }}>No channels yet — create one above</p> : channels.map(g=><GroupItem key={g.id} g={g}/>)}
          </div>
          <div className="px-3 pb-1 pt-3">
            <p className="text-xs font-bold uppercase tracking-wider mb-1.5 px-1" style={{ color:'var(--text-secondary)' }}>Direct Messages ({dms.length})</p>
            {dms.length===0 ? <p className="text-xs px-2 py-2 italic" style={{ color:'var(--text-secondary)' }}>No DMs yet — click "Direct Message"</p> : dms.map(g=><GroupItem key={g.id} g={g}/>)}
          </div>
        </div>
      </div>

      {/* ── Main panel ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ color:'var(--text-secondary)' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background:'var(--surface-alt)' }}><Users size={16}/></div>
            <p className="font-semibold text-base" style={{ color:'var(--text)' }}>Select a conversation</p>
            <p className="text-sm">Choose a channel or DM from the sidebar</p>
            <button onClick={()=>setSidebarOpen(true)} className="btn-secondary text-sm mt-2 lg:hidden">Open Sidebar</button>
          </div>
        ) : (<>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor:'var(--border)' }}>
            <button onClick={()=>setSidebarOpen(true)} className="lg:hidden p-1.5 rounded hover:bg-[var(--surface-alt)]"><Menu size={18}/></button>
            {active.type==='group'
              ? <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:'rgba(99,102,241,.15)',color:'#2E539F' }}><Hash size={14}/></div>
              : <Av name={getDMName(active)} color={getDMColor(active)} size="md" />
            }
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm" style={{ color:'var(--text)' }}>{getDMName(active)}</p>
              <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                {typingLabel || active.description || `${(active.members||[]).length} member${(active.members||[]).length!==1?'s':''}`}
              </p>
            </div>
            <div className="flex -space-x-1.5">
              {(active.members||[]).slice(0,5).map(m=><Av key={m.id} name={m.name} color={m.avatar_color}/>)}
              {(active.members||[]).length>5 && <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background:'#2E539F' }}>+{active.members.length-5}</div>}
            </div>
            {active.type==='group' && (
              <button onClick={()=>setShowManage(v=>!v)}
                className={`p-2 rounded-lg transition-colors ${showManage?'bg-brand-500/15 text-brand-500':'hover:bg-[var(--surface-alt)]'}`}
                style={!showManage?{color:'var(--text-secondary)'}:{}} title="Manage channel"><Settings size={14}/></button>
            )}
          </div>

          {/* Manage panel */}
          {showManage && active.type==='group' && (
            <div className="border-b px-5 py-4" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold text-sm mb-1" style={{ color:'var(--text)' }}>#{active.name}</p>
                  <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                    {canDelete(active) ? (isAdminRole(user?.role)&&active.created_by!==user?.id ? '⚙️ You can delete this channel as Admin' : '⚙️ You created this channel') : '🔒 Only the channel creator or an Admin can delete channels'}
                  </p>
                </div>
                {canDelete(active) && (!deleteConfirm
                  ? <button onClick={()=>setDeleteConfirm(true)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-red-500 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><TrashLg /> Delete Channel</button>
                  : <div className="flex items-center gap-2">
                      <p className="text-xs text-red-500 font-medium">Delete <strong>#{active.name}</strong>? Cannot be undone.</p>
                      <button onClick={deleteChannel} className="text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 font-semibold">Yes, delete</button>
                      <button onClick={()=>setDeleteConfirm(false)} className="text-xs px-3 py-1.5 rounded-lg btn-secondary">Cancel</button>
                    </div>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-0.5">
            {msgLoading
              ? <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
              : messages.length===0
              ? <div className="flex flex-col items-center justify-center py-16 gap-2">
                  <p className="text-3xl">💬</p>
                  <p className="text-sm font-semibold" style={{ color:'var(--text)' }}>{active.type==='group'?`Welcome to #${active.name}!`:`Start a conversation with ${getDMName(active)}`}</p>
                  <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Be the first to send a message</p>
                </div>
              : messages.map((msg, idx) => {
                  const isMe   = msg.sender_id === user?.id;
                  const isGroup = active.type === 'group';
                  const showAv = !isMe && (idx===0 || messages[idx-1].sender_id!==msg.sender_id);
                  return (
                    <div key={msg.id}>
                      {showDiv(messages,idx) && (
                        <div className="flex items-center gap-3 my-4">
                          <div className="flex-1 h-px" style={{ background:'var(--border)' }}/>
                          <span className="text-xs px-2" style={{ color:'var(--text-secondary)' }}>{fmtDiv(msg.created_at)}</span>
                          <div className="flex-1 h-px" style={{ background:'var(--border)' }}/>
                        </div>
                      )}
                      <div className={`flex items-end gap-2 group ${isMe?'flex-row-reverse':''}`}>
                        <div className={`w-7 flex-shrink-0 ${showAv&&!isMe?'':'invisible'}`}>
                          {showAv&&!isMe&&<Av name={msg.sender_name} color={msg.sender_color}/>}
                        </div>
                        <div className={`max-w-[70%] flex flex-col ${isMe?'items-end':'items-start'}`}>
                          {showAv&&!isMe&&<p className="text-xs mb-0.5 ml-1 font-semibold" style={{ color:'var(--text-secondary)' }}>{msg.sender_name}</p>}
                          {msg.reply_to&&msg.reply_content&&(
                            <div className="text-xs px-3 py-1 rounded-t-lg mb-0.5 border-l-2 border-brand-400 opacity-70 max-w-full" style={{ background:'var(--surface-alt)',color:'var(--text-secondary)' }}>
                              <span className="font-medium">{msg.reply_sender_name}: </span>{msg.reply_content?.slice(0,80)}
                            </div>
                          )}
                          <div className={`px-3 py-2 rounded-2xl text-sm ${isMe?'rounded-br-sm':'rounded-bl-sm'}`}
                            style={{ background:isMe?'#2E539F':'var(--surface-alt)', color:isMe?'white':'var(--text)' }}>
                            {msg.message_type==='text'&&<p className="whitespace-pre-wrap break-words">{msg.content}</p>}
                            {msg.message_type==='image'&&<img src={`${msg.file_url}`} alt={msg.file_name} className="rounded-lg max-w-xs max-h-60 object-cover cursor-pointer" onClick={()=>window.open(`${msg.file_url}`,'_blank')}/>}
                            {msg.message_type==='video'&&(
                              <video controls className="rounded-lg max-w-xs max-h-60" src={`${msg.file_url}`} style={{ background:'#000' }} />
                            )}
                            {msg.message_type==='file'&&(
                              <a href={`${msg.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80">
                                <File size={16}/><span className="text-xs underline truncate max-w-[160px]">{msg.file_name}</span>
                              </a>
                            )}
                          </div>
                          <div className={`flex items-center gap-1 mt-0.5 mx-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>{fmtTime(msg.created_at)}</p>
                            {isMe && !isGroup && <DMTicks msg={msg} />}
                            {isGroup && (
                              <GroupReadReceipt
                                msg={msg}
                                groupMembers={active.members||[]}
                                currentUserId={user?.id}
                              />
                            )}
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 pb-5 transition-opacity">
                          <button onClick={()=>setReplyTo(msg)} className="p-1 rounded text-xs" style={{ color:'var(--text-secondary)' }} title="Reply">↩</button>
                          {(isMe||isAdminRole(user?.role))&&<button onClick={()=>delMsg(msg.id)} className="p-1 rounded text-red-400" title="Delete"><TrashIc /></button>}
                        </div>
                      </div>
                    </div>
                  );
                })
            }
            <div ref={endRef}/>
          </div>

          {/* Reply preview */}
          {replyTo&&(
            <div className="flex items-center gap-3 px-4 py-2 border-t" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <div className="flex-1 text-xs border-l-2 border-brand-400 pl-2" style={{ color:'var(--text-secondary)' }}>
                <span className="font-medium">{replyTo.sender_name}: </span>{replyTo.content?.slice(0,100)||replyTo.file_name}
              </div>
              <button onClick={()=>setReplyTo(null)} style={{ color:'var(--text-secondary)' }}><X size={14}/></button>
            </div>
          )}

          {/* File preview bar */}
          {pendingFile&&(
            <div className="px-4 py-2 border-t flex items-center gap-2" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <FileBadge file={pendingFile} onRemove={()=>setPendingFile(null)}/>
            </div>
          )}

          {/* Input bar */}
          <div className="flex items-end gap-2 px-4 py-3 border-t relative" style={{ borderColor:'var(--border)' }}>
            {mention && <MentionDropdown candidates={mentionCandidates} activeIndex={mentionIndex} onSelect={selectMention} />}
            {!mention && showEmoji && <EmojiPicker onSelect={insertEmoji} onClose={()=>setShowEmoji(false)}/>}
            <button onClick={()=>setShowEmoji(v=>!v)}
              className={`p-2 rounded-lg transition-colors flex-shrink-0 ${showEmoji?'text-brand-500 bg-brand-500/10':'hover:bg-[var(--surface-alt)]'}`}
              style={!showEmoji?{color:'var(--text-secondary)'}:{}} title="Emoji">
              <EmojiIc />
            </button>
            <button onClick={()=>fileRef.current?.click()}
              className="p-2 rounded-lg hover:bg-[var(--surface-alt)] transition-colors flex-shrink-0"
              style={{ color:'var(--text-secondary)' }} title="Attach file">
              <ClipIc />
            </button>
            <input ref={fileRef} type="file" className="hidden"
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z"
              onChange={e=>{ if(e.target.files[0]) setPendingFile(e.target.files[0]); e.target.value=''; }}/>
            <textarea ref={textRef}
              className="flex-1 rounded-xl px-3 py-2 text-sm resize-none outline-none border"
              style={{ background:'var(--surface-alt)', borderColor:'var(--border)', color:'var(--text)', maxHeight:120 }}
              placeholder={`Message ${getDMName(active)}… (type @ to mention)`}
              rows={1} value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
            />
            <button onClick={send} disabled={(!text.trim()&&!pendingFile)||sending}
              className="p-2 rounded-lg btn-primary flex-shrink-0 disabled:opacity-40"><Send size={16}/></button>
          </div>
        </>)}
      </div>

      {/* ── New Group Modal ─────────────────────────────────────────── */}
      {showNewGroup&&(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setShowNewGroup(false)}/>
          <div className="relative rounded-xl shadow-2xl w-full max-w-md p-6 z-10" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <h3 className="font-bold mb-4" style={{ color:'var(--text)' }}>Create Group Channel</h3>
            <div className="space-y-3">
              <div><label className="label">Channel Name *</label><input className="input" placeholder="e.g. project-alpha" value={newGroupName} onChange={e=>setNewGroupName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&createGroup()} autoFocus/></div>
              <div><label className="label">Description</label><input className="input" placeholder="What's this channel for?" value={newGroupDesc} onChange={e=>setNewGroupDesc(e.target.value)}/></div>
              <div>
                <label className="label">Add Members</label>
                <div className="max-h-44 overflow-y-auto space-y-0.5 border rounded-xl p-2" style={{ borderColor:'var(--border)' }}>
                  {allUsers.filter(u=>u.id!==user?.id&&u.is_active).map(u=>(
                    <label key={u.id} className="flex items-center gap-2.5 cursor-pointer p-2 rounded-lg hover:bg-[var(--surface-alt)] transition-colors">
                      <input type="checkbox" checked={selectedMembers.includes(u.id)} onChange={e=>setSelectedMembers(p=>e.target.checked?[...p,u.id]:p.filter(id=>id!==u.id))}/>
                      <div className="w-6 h-6 rounded flex items-center justify-center font-bold text-white text-xs" style={{ background:u.avatar_color||'#2E539F' }}>{u.name[0]}</div>
                      <span className="text-sm flex-1" style={{ color:'var(--text)' }}>{u.name}</span>
                      <span className="text-xs" style={{ color:'var(--text-secondary)' }}>{u.role}</span>
                    </label>
                  ))}
                </div>
                {selectedMembers.length>0&&<p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>{selectedMembers.length} member{selectedMembers.length>1?'s':''} selected (you are added automatically)</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={()=>{setShowNewGroup(false);setNewGroupName('');setNewGroupDesc('');setSelectedMembers([]);}} className="btn-secondary">Cancel</button>
              <button onClick={createGroup} disabled={!newGroupName.trim()||creating} className="btn-primary flex items-center gap-2">
                {creating&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}Create Channel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New DM Modal ─────────────────────────────────────────────── */}
      {showNewDM&&(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={()=>setShowNewDM(false)}/>
          <div className="relative rounded-xl shadow-2xl w-full max-w-sm p-6 z-10" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
            <h3 className="font-bold mb-1" style={{ color:'var(--text)' }}>New Direct Message</h3>
            <p className="text-xs mb-4" style={{ color:'var(--text-secondary)' }}>Select a team member to chat with</p>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {allUsers.filter(u=>u.id!==user?.id&&u.is_active).map(u=>{
                const ex = dms.find(g=>(g.members||[]).some(m=>m.id===u.id));
                return (
                  <button key={u.id} onClick={()=>{ if(ex){selectGroup(ex);setShowNewDM(false);}else createDM(u.id); }}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-[var(--surface-alt)] transition-colors">
                    <Av name={u.name} color={u.avatar_color}/>
                    <div className="text-left flex-1"><p className="text-sm font-medium" style={{ color:'var(--text)' }}>{u.name}</p><p className="text-xs" style={{ color:'var(--text-secondary)' }}>{u.role}</p></div>
                    {ex ? <span className="text-xs px-2 py-0.5 rounded-full text-brand-500 font-medium" style={{ background:'rgba(99,102,241,.1)' }}>Open</span>
                        : <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Message</span>}
                  </button>
                );
              })}
            </div>
            <button onClick={()=>setShowNewDM(false)} className="btn-secondary w-full mt-4">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
